import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Loader2, TrendingUp, Search, Zap, AlertCircle } from "lucide-react";

interface LeadData {
    id: string;
    nome: string;
    email: string;
    url: string;
    status: string;
    performanceScore: number | null;
    seoScore: number | null;
    createdAt: string;
}

export default function Relatorio() {
    const { id } = useParams<{ id: string }>();
    const [lead, setLead] = useState<LeadData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLead = async () => {
            try {
                const response = await fetch(`/api/diagnostico/relatorio.php?id=${id}`);
                const data = await response.json();

                if (!response.ok || !data.success) {
                    throw new Error(data.error || 'Relatório não encontrado');
                }

                setLead(data.lead);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Erro ao carregar relatório');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchLead();

            // Auto-refresh se ainda estiver processando
            const interval = setInterval(() => {
                if (lead?.status === 'PROCESSANDO') {
                    fetchLead();
                }
            }, 5000); // A cada 5 segundos

            return () => clearInterval(interval);
        }
    }, [id, lead?.status]);

    const getScoreColor = (score: number | null) => {
        if (!score) return 'text-muted-foreground';
        if (score >= 90) return 'text-green-500';
        if (score >= 50) return 'text-yellow-500';
        return 'text-red-500';
    };

    const getScoreLabel = (score: number | null) => {
        if (!score) return 'N/A';
        if (score >= 90) return 'Excelente';
        if (score >= 50) return 'Precisa Melhorar';
        return 'Crítico';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Carregando relatório...</p>
                </div>
            </div>
        );
    }

    if (error || !lead) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <Card className="max-w-md w-full border-destructive/20">
                    <CardHeader>
                        <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                            <AlertCircle className="h-8 w-8 text-destructive" />
                        </div>
                        <CardTitle className="text-center">Relatório não encontrado</CardTitle>
                        <CardDescription className="text-center">
                            {error || 'O relatório solicitado não existe ou foi removido.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button asChild className="w-full">
                            <Link to="/diagnostico-gratuito">
                                Solicitar novo diagnóstico
                            </Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (lead.status === 'PROCESSANDO') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center px-4">
                <Card className="max-w-md w-full border-primary/20">
                    <CardHeader className="text-center">
                        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                        <CardTitle>Seu relatório está sendo gerado</CardTitle>
                        <CardDescription>
                            Estamos analisando <strong>{lead.url}</strong>
                            <br />
                            Por favor, aguarde alguns instantes...
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <Progress value={33} className="h-2" />
                            <p className="text-sm text-center text-muted-foreground">
                                Esta página será atualizada automaticamente
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
            </div>

            {/* Header */}
            <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                    <Link to="/">
                        <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
                    </Link>
                    <Button asChild variant="outline">
                        <Link to="/">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Voltar
                        </Link>
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-16 relative z-10">
                <div className="max-w-4xl mx-auto space-y-8">
                    {/* Header */}
                    <div className="text-center">
                        <h1 className="text-4xl font-bold mb-2">
                            Olá, {lead.nome}! 👋
                        </h1>
                        <p className="text-xl text-muted-foreground">
                            Aqui está o diagnóstico completo do site <strong className="text-foreground">{lead.url}</strong>
                        </p>
                    </div>

                    {/* Scores Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Performance Score */}
                        <Card className="border-primary/20">
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Zap className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <CardTitle>Performance</CardTitle>
                                        <CardDescription>Velocidade de carregamento</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-center space-y-4">
                                    <div className={`text-6xl font-bold ${getScoreColor(lead.performanceScore)}`}>
                                        {lead.performanceScore || 'N/A'}
                                    </div>
                                    <Progress value={lead.performanceScore || 0} className="h-3" />
                                    <p className="text-sm font-medium">
                                        {getScoreLabel(lead.performanceScore)}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* SEO Score */}
                        <Card className="border-primary/20">
                            <CardHeader>
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Search className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <CardTitle>SEO</CardTitle>
                                        <CardDescription>Otimização para buscadores</CardDescription>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-center space-y-4">
                                    <div className={`text-6xl font-bold ${getScoreColor(lead.seoScore)}`}>
                                        {lead.seoScore || 'N/A'}
                                    </div>
                                    <Progress value={lead.seoScore || 0} className="h-3" />
                                    <p className="text-sm font-medium">
                                        {getScoreLabel(lead.seoScore)}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Interpretation */}
                    <Card className="border-primary/20 bg-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <TrendingUp className="h-5 w-5 text-primary" />
                                O que esses números significam?
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <h3 className="font-semibold mb-2">🚀 Performance (Velocidade)</h3>
                                <p className="text-sm text-muted-foreground">
                                    Sites rápidos convertem mais. Se seu score está abaixo de 90, você está perdendo clientes.
                                    A cada segundo extra de carregamento, você perde 7% das conversões.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-semibold mb-2">🔍 SEO (Visibilidade no Google)</h3>
                                <p className="text-sm text-muted-foreground">
                                    Um bom SEO coloca seu site no topo do Google. Score abaixo de 90 significa que seu site
                                    está invisível para potenciais clientes que buscam seus serviços.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* CTA */}
                    <Card className="border-primary bg-gradient-to-r from-primary/10 to-accent/10">
                        <CardHeader className="text-center">
                            <CardTitle className="text-2xl">
                                Quer um diagnóstico completo e manual de boas práticas?
                            </CardTitle>
                            <CardDescription className="text-base">
                                Contrate uma análise aprofundada e receba um guia técnico de implementação para seu site. O valor é descontado caso feche o projeto completo!
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button size="lg" asChild>
                                <Link to="/diagnostico-completo">
                                    Ver opções de análise completa
                                </Link>
                            </Button>
                            <Button size="lg" variant="outline" asChild>
                                <Link to="/orcamento">
                                    Solicitar proposta personalizada
                                </Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
